import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useCoachUnread } from '@/hooks/useCoachUnread'
import { useClerk } from '@/hooks/useClerk'
import { useDashboard } from '@pelvia/shared/hooks/patient/useDashboard'
import { useProfile } from '@pelvia/shared/hooks/patient/useProfile'
import { initialsFor, patientDisplayName } from '@pelvia/shared/lib/patient/displayName'
import { Toast, useToast, type ToastKind } from '@/components/Toast'
import PelviaLogo from '@/components/PelviaLogo'
import { identifyUser, resetAnalytics } from '@/lib/analytics'
import './patient.css'

export interface PatientOutletContext {
  showToast: (message: string, kind?: ToastKind) => void
}

// Legacy ?tab= query param mapped to new route paths.
const LEGACY_TAB_MAP: Record<string, string> = {
  briefing: '/patient',
  calendar: '/patient', // merged into dashboard; legacy ?tab=calendar now lands on the dashboard, which embeds the calendar.
  predictor: '/patient', // pattern analytics merged into the dashboard
  coach: '/patient/coach',
  settings: '/patient/settings',
}

export default function PatientLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user, isLoading: userLoading, refetch: refetchUser } = useCurrentUser()
  const { data: profileResp } = useProfile()
  const { data: dashboard } = useDashboard()
  const { clerk } = useClerk()
  const { toast, show: showToast } = useToast()

  const outletContext = useMemo<PatientOutletContext>(() => ({ showToast }), [showToast])

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const reauthTriesRef = useRef(0)

  useEffect(() => {
    if (userLoading || user) {
      reauthTriesRef.current = 0
      return
    }
    // Backend doesn't recognize the session. Do NOT hard-logout on the first
    // miss: right after sign-in the full-page redirect to /patient can fire
    // /api/current-user before clerk-js has finished hydrating the __session
    // cookie, and a single early miss would otherwise become a permanent
    // /patient <-> /login bounce. Retry a few times first.
    //
    // The retries run on a self-driving interval: a refetch that comes back
    // with the same `null` user changes none of this effect's deps, so a
    // retry-per-effect-run design fires once and then strands the user on the
    // loading screen forever, never reaching the redirect below.
    const timer = setInterval(() => {
      if (reauthTriesRef.current < 4) {
        reauthTriesRef.current += 1
        refetchUser()
        return
      }
      clearInterval(timer)
      // Still rejected after retries. If Clerk holds a live session, the backend
      // genuinely won't accept it — send to /login?reauth=1 so it clears the stale
      // session and shows a fresh form. If Clerk has no session either, the user
      // is simply logged out; a plain /login avoids the needless sign-out.
      const hasClerkSession = Boolean(clerk && (clerk as { user?: unknown }).user)
      window.location.href = hasClerkSession ? '/login?reauth=1' : '/login'
    }, 700)
    return () => clearInterval(timer)
  }, [user, userLoading, clerk, refetchUser])

  // Tie analytics to the signed-in user once it loads. PostHog dedupes repeat
  // identify calls for the same distinct_id, so re-running on user changes is safe.
  useEffect(() => {
    if (user) {
      identifyUser({
        id: user.id,
        user_type: user.user_type,
      })
    }
  }, [user])

  // New users land on the full-screen intake flow until they complete it.
  // The intake route itself isn't nested under PatientLayout, so we can safely
  // redirect from any patient child route.
  useEffect(() => {
    if (dashboard?.intake_completed === false) {
      navigate('/patient/intake', { replace: true })
    }
  }, [dashboard?.intake_completed, navigate])

  // Legacy ?tab= deep-link support — redirect once to the mapped route.
  useEffect(() => {
    const search = new URLSearchParams(location.search)
    const tab = search.get('tab')
    if (!tab) return
    const target = LEGACY_TAB_MAP[tab]
    if (target) {
      navigate(target, { replace: true })
    }
  }, [location.search, navigate])

  // Close avatar menu on route change.
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Close avatar menu on outside click.
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  // Prefer the user's chosen Settings display name over their sign-up full name,
  // so renaming in Settings propagates to the avatar + menu immediately.
  const displayName = useMemo(() => {
    if (!user) return ''
    return patientDisplayName({
      displayName: profileResp?.profile?.display_name,
      fullName: user.full_name,
      email: user.email,
    })
  }, [user, profileResp?.profile?.display_name])

  const avatar = useMemo(() => (user ? initialsFor(displayName) : '?'), [user, displayName])
  const streak = dashboard?.streak?.current_streak ?? 0
  const coachUnread = useCoachUnread()

  const handleLogout = async () => {
    try {
      resetAnalytics()
      await clerk?.signOut?.()
    } finally {
      window.location.href = '/'
    }
  }

  // The Coach FAB is redundant on the coach page itself and inside the
  // check-in flow (no room, user is mid-task).
  const hideCoachFab =
    location.pathname === '/patient/coach' || location.pathname === '/patient/checkin'

  // Hide the FAB while actively scrolling so it doesn't cover content the
  // user is trying to read. Reveal it when scrolling stops or scrolling up.
  // These hooks must run on every render — keep them above any early return.
  const [fabScrollHidden, setFabScrollHidden] = useState(false)
  useEffect(() => {
    if (hideCoachFab) return
    let lastY = window.scrollY
    let stillTimer: number | undefined
    const onScroll = () => {
      const y = window.scrollY
      const dy = y - lastY
      if (dy > 4 && y > 80) {
        setFabScrollHidden(true)
      } else if (dy < -4) {
        setFabScrollHidden(false)
      }
      lastY = y
      if (stillTimer) window.clearTimeout(stillTimer)
      stillTimer = window.setTimeout(() => setFabScrollHidden(false), 600)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (stillTimer) window.clearTimeout(stillTimer)
    }
  }, [hideCoachFab, location.pathname])

  // Reset reveal state on every route change so users land on a new page with
  // the FAB visible.
  useEffect(() => {
    setFabScrollHidden(false)
  }, [location.pathname])

  if (userLoading || !user) {
    return (
      <div className="patient-loading">
        <div className="patient-loading-logo">
          <PelviaLogo size={96} animation="loading" withText />
        </div>
        <span className="patient-loading-label">Loading your dashboard</span>
      </div>
    )
  }

  // Show an explicit "back to dashboard" affordance everywhere except the
  // dashboard itself — the brand alone isn't obviously interactive.
  const showBackToHome = location.pathname !== '/patient'

  return (
    <div className="patient-shell">
      <header className="app-topbar">
        <div className="app-topbar-left">
          <Link to="/patient" className="app-topbar-brand" aria-label="Back to dashboard" title="Back to dashboard">
            <PelviaLogo size={40} animation="breathing" alt="" />
            <span>Pelvia</span>
          </Link>
          <AnimatePresence initial={false}>
            {showBackToHome && (
              <motion.div
                key="back-wrap"
                className="app-topbar-back-wrap"
                initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                animate={{ opacity: 1, width: 'auto', marginLeft: 8 }}
                exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                transition={{ type: 'spring', stiffness: 320, damping: 30 }}
              >
                <span className="app-topbar-divider" aria-hidden />
                <BackToDashboardLink />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="app-topbar-spacer" />
        <div className="app-topbar-avatar-wrap" ref={menuRef}>
          <motion.button
            type="button"
            className="app-topbar-avatar"
            aria-label="Profile menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            whileTap={{ scale: 0.92 }}
          >
            {avatar}
          </motion.button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="app-topbar-menu"
                role="menu"
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 420, damping: 32 }}
              >
                <div className="app-topbar-menu-header">
                  <div className="app-topbar-menu-name">{displayName}</div>
                  {streak > 0 && (
                    <div className="app-topbar-menu-streak">{streak} day streak</div>
                  )}
                </div>
                <Link to="/patient/profile" className="app-topbar-menu-item" role="menuitem">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
                    <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
                    <circle cx="20" cy="10" r="2" />
                  </svg>
                  Your health profile
                </Link>
                <Link to="/patient/settings" className="app-topbar-menu-item" role="menuitem">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  App settings
                </Link>
                <button
                  type="button"
                  className="app-topbar-menu-item app-topbar-menu-item-danger"
                  onClick={handleLogout}
                  role="menuitem"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Sign out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <main className="main-content">
        <div className={`content-area${hideCoachFab ? ' content-area-flush' : ''}`}>
          <Outlet context={outletContext} />
        </div>
      </main>

      {!hideCoachFab && <CoachFab hidden={fabScrollHidden} unread={coachUnread} />}

      <Toast toast={toast} />
    </div>
  )
}

function CoachFab({ hidden, unread }: { hidden?: boolean; unread?: boolean }) {
  return (
    <Link
      to="/patient/coach"
      className={`coach-fab${hidden ? ' coach-fab-hidden' : ''}`}
      aria-label="Ask your AI Coach"
      aria-hidden={hidden}
      tabIndex={hidden ? -1 : undefined}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <span>Ask Coach</span>
      {unread && <span className="coach-fab-unread-dot" aria-label="Unread message" />}
    </Link>
  )
}

// Back-to-dashboard link with a little animated arrow that slides left on hover,
// inspired by animate-ui's hover-choreography patterns (implemented in
// motion/react, which is already in the bundle).
function BackToDashboardLink() {
  const [hover, setHover] = useState(false)
  return (
    <Link
      to="/patient"
      className="app-topbar-back"
      aria-label="Back to dashboard"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
    >
      <motion.svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        animate={{ x: hover ? -3 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 22 }}
      >
        <motion.path
          d="M19 12H5"
          initial={false}
          animate={{ pathLength: hover ? 1 : 0.7, opacity: hover ? 1 : 0.85 }}
          transition={{ duration: 0.25 }}
        />
        <polyline points="12 19 5 12 12 5" />
      </motion.svg>
      <span>Dashboard</span>
    </Link>
  )
}
